import React, {useEffect, useMemo, useState} from 'react'
import {Backdrop, CircularProgress, Container, darken} from "@mui/material";
import {RegistrationNumberInput} from "../Components/SelfService/RegistrationNumberInput";
import UIActions from "../Redux/uiRedux";
import {useDispatch, useSelector} from "react-redux";
import { Theme } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import {SelfServiceLogin} from "../Components/SelfService/SelfServiceLogin";
import ActionSelector from "../Components/SelfService/ActionSelector";
import InspectionReminder from "../Components/SelfService/InspectionReminder/InspectionReminder";
import OtherServices from "../Components/SelfService/OtherServices/OtherServices";
import {useApiKey, useHistory, useSelfServiceThemeOptions} from '../Utils/hooks'
import {ThemeType} from '../interfaces'
import {HeaderContent} from '../Components/Header'
import {RootState} from '../Redux'
import {getLocale} from '../Utils/helpers'
import {LoginCredentialsType} from '../Utils/types'
import CloseButton from "../Components/SelfService/Common/CloseButton";
import CustomerTokenButton from '../Components/SelfService/Common/CustomerTokenButton'

interface SelfServiceContainerProps {
    themeName: string
}

interface StyleProps extends ThemeType {
}

const useLayoutStyles = makeStyles<Partial<StyleProps>>()((theme: Theme, styleProps: StyleProps) => ({
    root: {
        backgroundColor: theme.palette.background.default,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        zIndex: 0,
        [theme.breakpoints.down('sm')]: {
            overflowX: 'hidden',
        },
        '&:before': {
            content: '""',
            backgroundImage: styleProps.backgroundImage ? `url(${styleProps.backgroundImage})` : undefined,
            backgroundRepeat: 'repeat-y',
            backgroundSize: 'cover',
            position: 'fixed',
            opacity: styleProps.backgroundImageOverlay ? undefined : (styleProps.backgroundOpacity ?? 0.25),
            mixBlendMode: styleProps.backgroundImageOverlay ? undefined : styleProps.backgroundBlendMode,
            width: '100vw',
            height: '100vh',
            zIndex: -10,
            filter: styleProps.backgroundFilter,
        },
    },
    backgroundOverlay: styleProps.backgroundImageOverlay ? {
        backgroundColor: styleProps.backgroundBlendColor ?? theme.palette.background.default,
        opacity: (styleProps.backgroundOpacity ?? 0.25),
        mixBlendMode: styleProps.backgroundBlendMode,
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: -9,
    } : {
        display: 'none',
    }
}))

const useStyles = makeStyles<ThemeType>()((theme: Theme, options: ThemeType) => ({
    backdrop: {
        zIndex: 1000,
    },
    backgroundContainer: options.scrollingBackgroundImage ? {
        backgroundImage: `url(${options.scrollingBackgroundImage})`,
        backgroundSize: '100% auto',
        backgroundRepeat: 'repeat-y',
        backgroundPosition: '0 0',
    } : {},
    headerRow: {
        height: 65,
        width: 'calc(100vw - 80px)',
        minWidth: '90vw',
        margin: 'auto',
        paddingTop: 26,
        backgroundColor: options.headerBackgroundColor,
    },
    container: {
    },
    content: {
        padding: theme.spacing(3),
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginBottom: theme.spacing(2),
            padding: theme.spacing(2),
        },
        boxShadow: theme.shadows[6],
        outline: 'none',
        position: 'relative',
        borderRadius: 24,
        zIndex: 0,
        '&:before': {
            backgroundColor: '#fff',
            content: '""',
            position: 'absolute',
            borderRadius: 24,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: -1,
        },
    },
}))

export const SelfServiceContainer = (props: SelfServiceContainerProps) => {
    const history = useHistory()
    const language = getLocale()
    const themeOptions = useSelfServiceThemeOptions()
    const layoutStyles = useLayoutStyles(themeOptions)
    const { classes } = useStyles(themeOptions)
    const { t } = useTranslation()

    const [selectedAction, setSelectedAction] = useState(null);

    const dispatch = useDispatch();

    const apiKey: LoginCredentialsType = useApiKey();
    const showCloseButton = selectedAction !== null
    const chainLogo = useSelector((state: RootState) => state.ui?.settings?.chain?.logo?.data)
    const stationLogo = useSelector((state: RootState) => state.ui?.settings?.station?.logo?.data)
    const isConfigured = useSelector((state: RootState) => !! state.ui?.settings?.station?.id)

    const getTitle = () => {
        return t('selfService.title')
    }

    useEffect(() => {
        const chainId: string = apiKey.chainId;
        const stationId: string  = apiKey.stationId;
        const embedded = false;
        if (apiKey.apiKey !== null && apiKey.chainId !== null && apiKey.stationId !== null) {
            dispatch(UIActions.getSettings(chainId, stationId, history, embedded, undefined))
        }
    }, [apiKey]);

    const commonStyles = useMemo(() => (theme) => ({
        '& .MuiButton-root': {
            textTransform: 'none',
            borderStyle: 'solid',
            borderWidth: 2,
            borderRadius: '0.5rem',
            boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.2)',
        },
        '& .MuiButton-text': {
            borderWidth: 0,
            boxShadow: 'none',
        },
        '& .MuiButton-containedPrimary': {
            borderColor: themeOptions.primaryBorderColor ?? theme.palette.primary.contrastText,
            backgroundColor: themeOptions.primaryButtonColor ?? theme.palette.primary.main,
            color: themeOptions.primaryButtonTextColor ?? theme.palette.primary.contrastText,
            '& .MuiSvgIcon-root': {
                color: themeOptions.primaryButtonTextColor ?? theme.palette.primary.contrastText,
            },
        },
        '& .MuiButton-containedSecondary': {
            borderColor: themeOptions.secondaryBorderColor ?? theme.palette.secondary.main,
            backgroundColor: themeOptions.secondaryButtonColor ?? theme.palette.secondary.main,
            color: themeOptions.secondaryButtonTextColor ?? theme.palette.secondary.contrastText,
            '& .MuiSvgIcon-root': {
                color: themeOptions.secondaryButtonTextColor ?? theme.palette.secondary.contrastText,
            },
        },
        '& .MuiButton-containedPrimary:hover': {
            boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.2)',
        },
        '& .MuiButton-containedSecondary:hover': {
            boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.2)',
        },
        '& .MuiButton-sizeLarge': {
            minHeight: 130,
            minWidth: 390,
            paddingLeft: '2em',
            paddingRight: '2em',
            fontSize: '2.2rem',
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                minHeight: 115,
                minWidth: 115,
                fontSize: '1.85rem',
            },
            [theme.breakpoints.down('sm')]: {
                minHeight: 90,
                minWidth: 90,
                fontSize: '1.5rem',
            },
        },
        '& .MuiButton-sizeLarge.buttonNarrow': {
            minWidth: 130,
        },
        '& .MuiButton-sizeMedium': {
            minHeight: 100,
            minWidth: 300,
            paddingLeft: '1.5em',
            paddingRight: '1.5em',
            fontSize: '1.8rem',
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                minHeight: 85,
                minWidth: 85,
                fontSize: '1.5rem',
            },
            [theme.breakpoints.down('sm')]: {
                minHeight: 70,
                minWidth: 70,
                fontSize: '1.2rem',
            },
        },
        '& .MuiButton-sizeMedium.buttonNarrow': {
            minWidth: 100,
        },
        '& .MuiButton-sizeSmall': {
            minHeight: 80,
            minWidth: 240,
            paddingLeft: '1em',
            paddingRight: '1em',
            fontSize: '1.5rem',
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                minHeight: 70,
                minWidth: 70,
                fontSize: '1.2rem',
            },
            [theme.breakpoints.down('sm')]: {
                minHeight: 60,
                minWidth: 60,
                fontSize: '1.0rem',
            },
        },
        '& .MuiButton-sizeSmall.buttonNarrow': {
            minWidth: 80,
        },
        '& .MuiButton-sizeSmall.buttonCompact': {
            minHeight: 50,
            lineHeight: 1,
        },
        '& .MuiButton-sizeSmall.buttonCompactIcon': {
            minHeight: 50,
            minWidth: 50,
            width: 50,
            lineHeight: 1,
        },
        '& .textButtonCompact': {
            minHeight: 0,
            minWidth: 0,
            padding: 0,
            fontSize: '1rem',
            color: themeOptions.primaryLinkColor ?? theme.palette.primary.main,
            textDecoration: 'underline',
        },
        '& .MuiCheckbox-colorPrimary': {
            '& .MuiSvgIcon-root': {
                textDecoration: 'underline',
                color: themeOptions.primaryButtonColor ?? theme.palette.primary.main,
            },
        },
        '& .MuiCheckbox-colorSecondary': {
            '& .MuiSvgIcon-root': {
                textDecoration: 'underline',
                color: themeOptions.secondaryButtonColor ?? theme.palette.secondary.main,
            },
        },
        '& .MuiInputBase-root': {
            fontSize: '1.25rem',
            lineHeight: 1.25,
            borderRadius: '0.5rem',
        },
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#eee',
            boxShadow: '2px 2px 8px 0px rgba(0,0,0,0.2)',
            color: '#333',
            borderColor: themeOptions.primaryBorderColor,
            '& fieldset': {
                borderWidth: 2,
                borderColor: themeOptions.primaryBorderColor,
            },
            '&:hover fieldset': {
                borderWidth: 2,
                borderColor: themeOptions.primaryBorderColor,
            },
            '&.Mui-focused fieldset': {
                borderWidth: 2,
                borderColor: themeOptions.primaryBorderColor,
            },
            '&.Mui-error fieldset': {
                borderWidth: 2,
                borderColor: themeOptions.primaryBorderColor,
            },
        },
        '& .MuiFormHelperText-root' : {
            color: theme.palette.primary.main,
            '&.Mui-error': {
                color: themeOptions.errorMainColor ?? theme.palette.error.main,
            },
        },
        '& .registrationNumber': {
            fontSize: '7rem',
            fontFamily: 'Koulen',
            fontWeight: 500,
        },
        '& .registrationNumberInput': {
            height: 100,
            '& input': {
                height: 60,
                width: 320,
                fontSize: '4rem',
                paddingLeft: '1rem',
                fontFamily: 'Koulen',
                fontWeight: 500,
                lineHeight: 1.0
            }
        },
        '& .pageMainContainer': {
            height: '100%',
            width: 1200,
            maxWidth: 'calc(100vw - 80px)',
            padding: 0,
            margin: 'auto',
        },
        '& .pageScrollContainer': {
            width: '100%',
            overflowY: 'auto',
        },
        '& .pageMainHeader': {
            display: 'flex',
            textAlign: 'center',
            justifyContent: 'center',
            alignItems: 'end',
            height: '15vh',
            minHeight: 50,
        },
        '& .contentWithMargin': {
            justifyContent: 'center',
            textAlign: 'center',
            width: '900px',
            maxWidth: '80vw',
        },
        '& .footerButtons': {
            width: 'calc(100vw - 80px)',
            minWidth: '90vw',
        },
        '&. buttonRowCentered': {
            alignContent: 'center',
        },
        '& .selectedServiceButton': {
            backgroundColor: themeOptions.secondaryButtonColor ?? theme.palette.secondary.contrastText,
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            minHeight: 50,
            lineHeight: 1,
        },
        '& .selectedServiceButton:hover': {
            backgroundColor: themeOptions.secondaryButtonColor ?? theme.palette.secondary.contrastText,
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            minHeight: 50,
            lineHeight: 1,
        },
        '& .serviceButton': {
            minHeight: 50,
            lineHeight: 1,
        },
        '& .reservationPaymentDoneDate': {
            fontWeight: 600,
        },
    }), [themeOptions])

    if (apiKey.apiKey && !isConfigured) {
        return (
            <Backdrop className={classes.backdrop} open={true} style={{ backgroundColor: 'white' }}>
                <CircularProgress size={48} color="info" />
            </Backdrop>
        )
    }

    return (
        <div className={layoutStyles.classes.root}>
            <div className={layoutStyles.classes.backgroundOverlay}></div>
            <Helmet>
                <html lang={language}/>
                <title>{getTitle()}</title>
                <link rel="manifest" href={`${process.env.PUBLIC_URL}/manifest.json`}/>

                <link rel="preconnect" href="https://fonts.googleapis.com"/>
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"/>
                <link href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap" rel="stylesheet"/>

                <link rel="preconnect" href="https://fonts.googleapis.com"/>
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous"/>
                <link href="https://fonts.googleapis.com/css2?family=Koulen&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className={classes.headerRow}>
                <HeaderContent
                    title={getTitle()}
                    logo={stationLogo || chainLogo}
                    wwwAddress={''}
                    display={true}
                    renderTimer={null}
                    selfService={true}
                    showLogo={true}
                    showCloseButton={showCloseButton}
                    closeButton={() => {return <CloseButton sx={commonStyles}/>}}
                />
            </div>
            <div className={classes.backgroundContainer}>
                <Container maxWidth={false}
                           disableGutters
                           classes={{
                               root: classes.container,
                           }}
                           sx={commonStyles}
                >
                    <div className={themeOptions.selfService ? null : classes.content} style={{padding: 0, margin: 0, paddingBottom: 32, height: 'calc(100vh - 65px)'}}>
                        {apiKey.apiKey ?
                            <>
                                {selectedAction === null &&
                                    <ActionSelector
                                        onSelect={setSelectedAction}
                                        footer={<CustomerTokenButton sx={commonStyles}/>}/>
                                }
                                {selectedAction === 'Action 1' &&
                                    <RegistrationNumberInput/>
                                }
                                {selectedAction === 'Action 2' &&
                                    <OtherServices/>
                                }
                                {selectedAction === 'Action 3' &&
                                    <InspectionReminder orderReminder={true} thankYouMessage={t('selfService.youHaveInspectionReminder')}/>
                                }
                            </>
                            :
                            <SelfServiceLogin/>
                        }
                    </div>
                </Container>
            </div>
        </div>
    )
}
